import { ref } from "vue";
import type { UIMessage } from "ai";
import * as Sentry from "@sentry/vue";
import { useToast } from "vue-toastification";
import { submitAiResponseRating } from "@/api/services/AiUsage";
import type { ResponseRatingController, ResponseRatingState } from "./responseRating";
import { INTERACTION_START_PART_TYPE, isValidRating, readInteractionId, shouldPromptForRating } from "../utils/responseRating";

/**
 * Per-conversation state behind the "How was this answer?" prompt (RTLCA-4748).
 *
 * Three things have to line up for a rating to be possible, and this composable owns all three:
 *
 * 1. WHICH USAGE ROW an assistant message maps to. The agent service mints a `requestId` per turn,
 *    reports the turn to the API's AI-usage controller under it, and streams it to us as the
 *    transient `data-interaction_start` part. Transient parts only reach `onData`, and they arrive
 *    before the SDK has created the assistant message stub — so the id is parked in `pending` and
 *    attached to the next assistant message the message list grows (`attachPendingInteractionId`,
 *    called from useAgentChat's existing deep watch). An approval-resume runs a second turn for the
 *    same message and simply overwrites the mapping with the id that produced the final answer.
 * 2. WHEN to ask. The first completed assistant response of the conversation, then every 5th
 *    (`shouldPromptForRating`), counted on the turn-settled edge so a streaming answer is never interrupted by the row. A response
 *    with no interaction id (usage tracking disabled) is counted but cannot be prompted — there is no
 *    row to write to.
 *    A prompt is one question about one answer: it goes away when the user sends their next message
 *    (they have moved on), and the component closes it a few seconds after a score is sent. A sent
 *    score is final — the leaves lock — so the row cannot be used to keep flipping a rating.
 * 3. WRITING it, through the API directly (`POST api/ai-usage/feedback`) — the same endpoint the
 *    agent service would proxy to, minus a hop. Best-effort and never blocking: a failure toasts and
 *    goes to Sentry, and the prompt stays so the user can retry.
 */
export function useResponseRating() {
  const toast = useToast();

  const interactionIdByMessageId = new Map<string, string>();
  let pendingInteractionId: string | null = null;

  // Ref-of-record rather than a reactive Map so `promptFor` is trivially reactive from a computed.
  const prompts = ref<Record<string, ResponseRatingState>>({});
  const countedMessageIds = new Set<string>();
  let completedResponses = 0;

  /** Feed every part the SDK hands to `onData`; only the interaction_start part is read. */
  function onStreamData(part: { type: string; data?: unknown }) {
    if (part.type !== INTERACTION_START_PART_TYPE) return;
    pendingInteractionId = readInteractionId(part.data);
  }

  /** Call whenever the SDK's message list changes: hands the parked id to the assistant message being generated. */
  function attachPendingInteractionId(messages: readonly UIMessage[]) {
    if (!pendingInteractionId) return;
    const last = messages.at(-1);
    if (!last || last.role !== "assistant") return;
    interactionIdByMessageId.set(last.id, pendingInteractionId);
    pendingInteractionId = null;
  }

  /** Call on the turn-settled edge: counts the response and opens a prompt when the cadence says so. */
  function onTurnSettled(messages: readonly UIMessage[]) {
    const last = messages.at(-1);
    if (!last || last.role !== "assistant" || countedMessageIds.has(last.id)) return;
    countedMessageIds.add(last.id);
    completedResponses += 1;

    const interactionId = interactionIdByMessageId.get(last.id);
    if (!interactionId || !shouldPromptForRating(completedResponses)) return;
    prompts.value = { ...prompts.value, [last.id]: { interactionId, status: "idle", rating: null } };
  }

  function reset() {
    interactionIdByMessageId.clear();
    pendingInteractionId = null;
    countedMessageIds.clear();
    completedResponses = 0;
    prompts.value = {};
  }

  function setPrompt(messageId: string, patch: Partial<ResponseRatingState>) {
    const current = prompts.value[messageId];
    if (!current) return;
    prompts.value = { ...prompts.value, [messageId]: { ...current, ...patch } };
  }

  const controller: ResponseRatingController = {
    promptFor: (messageId) => prompts.value[messageId] ?? null,

    submit: async (messageId, rating, comment) => {
      const prompt = prompts.value[messageId];
      if (!prompt || prompt.status === "sending" || !isValidRating(rating)) return;
      setPrompt(messageId, { status: "sending" });
      try {
        await submitAiResponseRating({ requestId: prompt.interactionId, rating, comment });
        setPrompt(messageId, { status: "sent", rating });
      } catch (error) {
        // Back to where the user was (a first attempt returns to idle, a comment retry keeps the score)
        // so the row is still there to try again.
        setPrompt(messageId, { status: prompt.status === "sent" ? "sent" : "idle" });
        Sentry.captureException(error);
        toast.error("Could not send your rating. Please try again.");
      }
    },

    dismiss: (messageId) => {
      const { [messageId]: _dismissed, ...rest } = prompts.value;
      prompts.value = rest;
    },

    dismissAll: () => {
      prompts.value = {};
    },
  };

  return { controller, onStreamData, attachPendingInteractionId, onTurnSettled, reset };
}
